import { getShouldQualifyOfferHotelCancellation } from 'selectors/hotelCancellationSelectors'
import { hasAirportTransferPackageInclusions } from 'lib/offer/airportTransferPackageGrouping'
import { useCallback, useContext, useMemo, lazy, Suspense } from 'react'
import * as React from 'react'
import moment from 'moment'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions, getEffectiveCharterBundleRoute, isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import AccommodationSameRoomOptionTile from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTile'
import OfferPageFocusAnchor from 'components/OfferPage/OfferPageFocusAnchor'
import AccommodationSameRoomOptionTileDetails from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileDetails'
import AccommodationSameRoomOptionTilePricing from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTilePricing'
import AccommodationSameRoomOptionTileCTA from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileCTA'
import AccommodationSameRoomOptionTileHeaderArea from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileHeaderArea'
import FlashHotelOptionPriceDetails from 'components/OfferPage/FlashHotel/FlashHotelOptionPriceDetails'
import FlashPackageSelectionPriceDetails from '../packageSelection/FlashPackageSelectionPriceDetails'
import type { PackageSelectionPricing } from 'components/OfferPage/packageSelection/packageSelectionPricing'
import FlashHotelInclusionsToggle from 'components/OfferPage/FlashHotel/FlashHotelInclusionsToggle'
import FlashHotelRateInclusionsContent from 'components/OfferPage/FlashHotel/FlashHotelRateInclusionsContent'
import useFlashHotelRateInclusions, { hasPackageInclusionItems } from 'components/OfferPage/FlashHotel/useFlashHotelRateInclusions'
import useLoyaltyOfferPageTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetStayUpgrade'
import getLoyaltyUpgradeTargetDisplayName from 'loyalty/lib/benefits/getLoyaltyUpgradeTargetDisplayName'
import useLoyaltyTargetUpgradePackages from 'loyalty/hooks/useLoyaltyTargetUpgradePackages'
import useLoyaltyOfferPageClearTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageClearTargetStayUpgrade'
import OfferPageLoyaltyStayUpgradeCallOutBanner from 'components/OfferPage/Loyalty/OfferPageLoyaltyStayUpgradeCallOutBanner'
import OfferPageTierProgressionCallOutBanner from 'components/OfferPage/Loyalty/OfferPageTierProgressionCallOutBanner'
import { useAppSelector } from 'hooks/reduxHooks'
import BodyText from 'LuxKit/Typography/BodyText'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import { tierDarkForegroundColorStyle } from 'styles/utils/tierStyles'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import useUserCanApplyLoyaltyBenefit from 'loyalty/hooks/useUserCanApplyLoyaltyBenefit'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import { AccommodationSameRoomOptionTileSubgrid } from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileSubgrid'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import SolidStarIcon from 'LuxKit/Icons/solid/SolidStarIcon'
import SuggestedPackagePromotionLabel from 'components/OfferPage/SuggestedPackagePromotionLabel'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { toBaseFareClass, type FlightsClassTypes } from 'constants/flight'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { useIntl } from 'react-intl'
import useRebrandVersion from 'hooks/useRebrandVersion'
import PackageInclusionsValidityText, { hasStructuredPackageInclusionsValidityText } from 'components/OfferPage/Accommodation/PackageInclusionsValidityText'
import AirportTransferOption from './AirportTransferOption'
import { getInclusionsWithoutPackageValidityText } from 'components/OfferPage/Accommodation/packageInclusionValidityUtils'
import useFlashHotelPackageOptionImpression from './useFlashHotelPackageOptionImpression'
import ModalContext from 'contexts/ModalContext'
import FlashHotelRoomDetailsModal from '../FlashHotelMobileVariant/Modals/FlashHotelRoomDetailsModal'
import AccommodationPackageSelectionTile from 'components/AccommodationKit/AccommodationPackageSelection/AccommodationPackageSelectionTile'
import FlashPackageRoomQuantity from './FlashPackageRoomQuantity'
import type { AccommodationRoomQuantityOption } from 'components/AccommodationKit/AccommodationPackageSelection/AccommodationRoomQuantityOptions'
import PackageSelectionHeading from 'components/OfferPage/packageSelection/PackageSelectionHeading'
import PackageSelectionRecommendation from 'components/OfferPage/packageSelection/PackageSelectionRecommendation'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import FlashPackageSelectionInclusions from '../packageSelection/FlashPackageSelectionInclusions'
import type { OfferPageBurnResult } from 'hooks/useOfferPageBurnPoints'
import OfferBookButton from 'components/OfferPage/OfferBookButtons/OfferBookButton'
import FlashPackageSelectionOptions from '../packageSelection/FlashPackageSelectionOptions'

const FlashHotelOptionCTAs = lazy(() => import('components/OfferPage/FlashHotel/FlashHotelOptionCTAs'))

const InclusionsContainer = styled.div`
  position: relative;
  display: grid;
  grid-template-areas: 'base-package' 'target-upgrade-package';
  grid-template-rows: 1fr;
`

const BasePackageInclusions = styled.div`
  grid-area: base-package;
  grid-row: 1;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 300ms ease, transform 300ms ease;
  transition-delay: 300ms;

  &:not(.show) {
    position: absolute;
    inset: 0;
    opacity: 0;
    transform: translateY(${rem(16)});
    transition-delay: 0ms;
    pointer-events: none;
  }
`

const UpgradedInclusionsHeading = styled(BodyText)`
  margin-block-end: ${rem(8)};
  ${tierDarkForegroundColorStyle}
`

const TargetUpgradePackageInclusions = styled.div`
  grid-area: target-upgrade-package;
  grid-row: 1;
  opacity: 1;
  transform: translateY(${rem(0)});
  transition: opacity 300ms ease, transform 300ms ease;
  transition-delay: 300ms;

  &:not(.show) {
    position: absolute;
    inset: 0;
    opacity: 0;
    transform: translateY(${rem(-16)});
    transition-delay: 0ms;
    pointer-events: none;
  }
`

const SuggestedPackageBannerContainer = styled.div`
  ${mediaQueryUp.tablet} {
    border-inline: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

const OptionDBooking = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: ${rem(8)};
`

interface Props {
  showHotelOnly: boolean
  offer: App.HotelOffer
  packageView: App.OfferPackageOptionView
  /** Every package option available for the selected duration, used to resolve an upgraded room. */
  packageOptions: Array<App.OfferPackageOptionView>
  displayPackageView?: App.OfferPackageOptionView
  /**
   * The canonical snapshot target id for this package row (e.g. `package:<id>`), when the
   * assistant focus feature should be able to scroll/highlight this exact row. Registered as a
   * child of the tile itself (`anchorRelation="parent"`, the default) rather than as a preceding
   * sibling, so this stays a single grid/carousel child -- a sibling anchor would double the
   * carousel's own child count and corrupt its scroll-snap/arrow-scroll math.
   */
  focusTargetId?: string
  packagePosition?: number
  checkIn?: string
  duration: number
  rooms: Array<OfferPageRoom>
  fareClass: FlightsClassTypes
  charterBundleRoute?: App.CharterBundleRoute
  flightOrigin?: App.Airport
  availableForPurchase?: boolean
  packageLoading: boolean
  roomsBeforeSurcharge?: number
  flights?: boolean
  isInclusionsValidityEnabled: boolean
  inclusionsOpen?: boolean
  onInclusionsToggle?: () => void
  inclusionsContentId?: string
  suggestedPackageMessage?: string
  airportTransferPriceDifference?: number
  airportTransferInclusion?: App.PackageInclusion
  airportTransferBasePackageKey?: string
  airportTransferSelected?: boolean
  onAirportTransferToggle?: (packageKey: string, event: React.ChangeEvent<HTMLInputElement>) => void
  showAllInclusions?: boolean
  presentation?: { kind: 'control' } | {
    kind: 'option-d'
    showOptions: boolean
    earnings: React.ReactNode
    burnResult: OfferPageBurnResult
    quoteScope: 'from' | 'selected'
    selectionPricing: PackageSelectionPricing
    occupancyOverride?: Array<OfferPageRoom>
    quantityFailure?: string
    purchaseDisabled?: boolean
    quantity: number
    selectionQuantity?: number
    roomSelected: boolean
    quantityOptions: Array<AccommodationRoomQuantityOption>
    quantityQuoteKey: string
    onQuantityChange: (quantity: number) => void
  }
  /**
   * Custom content to render in the pricing area.
   * If provided, replaces the default FlashHotelOptionPriceDetails.
   */
  children?: React.ReactNode
}

function FlashHotelRateOption(props: Props) {
  const {
    showHotelOnly,
    offer,
    packageView,
    packageOptions,
    displayPackageView = packageView,
    focusTargetId,
    packagePosition,
    checkIn,
    duration,
    rooms,
    fareClass,
    charterBundleRoute,
    flightOrigin,
    availableForPurchase,
    packageLoading,
    roomsBeforeSurcharge,
    flights,
    isInclusionsValidityEnabled,
    children,
    inclusionsOpen,
    onInclusionsToggle,
    inclusionsContentId,
    suggestedPackageMessage,
    airportTransferPriceDifference,
    airportTransferInclusion,
    airportTransferBasePackageKey,
    airportTransferSelected,
    onAirportTransferToggle,
    showAllInclusions,
    presentation,

  } = props

  const selectionPresentation = presentation?.kind === 'option-d' ? presentation : undefined
  const optionD = !!selectionPresentation
  const inclusionsDisclosure = useMemo(() => onInclusionsToggle ? {
    expanded: inclusionsOpen ?? false,
    onToggle: onInclusionsToggle,
    contentId: inclusionsContentId,
  } : undefined, [inclusionsContentId, inclusionsOpen, onInclusionsToggle])
  const { earnings, quoteScope, occupancyOverride, quantityFailure, quantity = 0, roomSelected = false, quantityOptions, quantityQuoteKey, onQuantityChange } = selectionPresentation ?? {}

  const intl = useIntl()
  const isRebrand = useRebrandVersion() === 'rebrand'
  const pkg = displayPackageView.package
  const suggestedPackagePromotionLabel = suggestedPackageMessage && isRebrand
    ? <SuggestedPackagePromotionLabel message={suggestedPackageMessage} />
    : undefined

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const checkOutMoment = useMemo(() => checkInMoment?.clone().add(duration, 'days'), [checkInMoment, duration])
  const checkOut = checkOutMoment?.format('YYYY-MM-DD')
  const { impressionRef, reportDisplayedPricing } = useFlashHotelPackageOptionImpression({
    offer,
    packageView,
    packageUniqueKey: pkg.uniqueKey,
    checkIn,
    checkOut,
    rooms: occupancyOverride ?? rooms,
  })
  const { departDate: charterDepartDate } = useCharterFlightDates({
    checkInDate: checkIn,
    nights: duration,
    charterBundleRoute,
    rooms,
    fareClass,
  })

  const { hasFeederFlightPriced } = useOfferPageFlightPrice()
  const {
    inclusions: visibleEffectiveInclusions,
    bonusInclusions: visibleBonusInclusions,
    luxPlusInclusions,
    hasInclusions,
    shouldShowInclusionsValidityText: shouldShowPackageInclusionsValidityText,
    selectedOccupancy,
    showReturnFlightsInclusion,
  } = useFlashHotelRateInclusions(pkg, {
    offer,
    checkIn: checkInMoment,
    checkOut: checkOutMoment,
    rooms: occupancyOverride ?? rooms,
    charterBundleRoute,
    flightOrigin,
    flights,
    hasFeederFlightPriced,
    isInclusionsValidityEnabled,
  })

  const {
    selectedTargetUpgradePackage,
    selectedTargetUpgradeType,
  } = useLoyaltyOfferPageTargetStayUpgrade(pkg)

  const appliedUpgradeDisplayName = selectedTargetUpgradeType === 'room' ?
      (selectedTargetUpgradePackage?.roomType?.name ?? selectedTargetUpgradePackage?.name ?? '') :
      getLoyaltyUpgradeTargetDisplayName(selectedTargetUpgradePackage?.name)

  const selectedLoyaltyTargetUpgrade = useMemo(() => {
    if (!selectedTargetUpgradePackage) return undefined
    return { name: appliedUpgradeDisplayName }
  }, [appliedUpgradeDisplayName, selectedTargetUpgradePackage])

  const displayTier = useAppSelector(getLoyaltyDisplayTier)
  const showModal = useContext(ModalContext)

  const upgradedRoomType = selectedTargetUpgradeType === 'room' ? selectedTargetUpgradePackage?.roomType : undefined

  // The details modal looks the room up among this duration's packages, so it has nothing to show
  // when the upgraded room has no package for these dates. Hide the link rather than open an empty modal.
  const canViewUpgradedRoomDetails = !!upgradedRoomType &&
    packageOptions.some(option => option.package.roomType?.id === upgradedRoomType.id)

  const onViewUpgradedRoomDetails = useCallback(() => {
    if (!upgradedRoomType) return
    showModal(<FlashHotelRoomDetailsModal room={upgradedRoomType} />)
  }, [showModal, upgradedRoomType])

  const upgradedInclusionsHeading = <UpgradedInclusionsHeading className={`tier-${displayTier}`} variant="medium" weight="bold">
    {intl.formatMessage({
      id: '66RRbkMfbOZmc0UUAruC',
      defaultMessage: 'Your upgraded inclusions',
      description: 'Heading above the inclusions a loyalty member gains from a free package upgrade',
    })}
  </UpgradedInclusionsHeading>

  const targetUpgradePackages = useLoyaltyTargetUpgradePackages(pkg, offer)
  const loyaltyRoomUpgradePkg = targetUpgradePackages?.room
  const loyaltyPackageUpgradePkg = targetUpgradePackages?.package

  const {
    inclusions: loyaltyRoomUpgradeInclusions,
    bonusInclusions: loyaltyRoomUpgradeBonusInclusions,
    luxPlusInclusions: loyaltyRoomUpgradeLuxPlusInclusions,
  } = useOfferInclusions(loyaltyRoomUpgradePkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const {
    inclusions: loyaltyPackageUpgradeInclusions,
    bonusInclusions: loyaltyPackageUpgradeBonusInclusions,
    luxPlusInclusions: loyaltyPackageUpgradeLuxPlusInclusions,
  } = useOfferInclusions(loyaltyPackageUpgradePkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const shouldShowLoyaltyRoomUpgradeInclusionsValidityText = isInclusionsValidityEnabled && !!loyaltyRoomUpgradePkg?.showInclusionsValidityTag && hasStructuredPackageInclusionsValidityText(loyaltyRoomUpgradePkg?.roomPolicyDescription)
  const shouldShowLoyaltyPackageUpgradeInclusionsValidityText = isInclusionsValidityEnabled && !!loyaltyPackageUpgradePkg?.showInclusionsValidityTag && hasStructuredPackageInclusionsValidityText(loyaltyPackageUpgradePkg?.roomPolicyDescription)
  const visibleLoyaltyRoomUpgradeInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(loyaltyRoomUpgradeInclusions, shouldShowLoyaltyRoomUpgradeInclusionsValidityText)
  }, [loyaltyRoomUpgradeInclusions, shouldShowLoyaltyRoomUpgradeInclusionsValidityText])
  const visibleLoyaltyRoomUpgradeBonusInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(loyaltyRoomUpgradeBonusInclusions, shouldShowLoyaltyRoomUpgradeInclusionsValidityText)
  }, [loyaltyRoomUpgradeBonusInclusions, shouldShowLoyaltyRoomUpgradeInclusionsValidityText])
  const visibleLoyaltyPackageUpgradeInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(loyaltyPackageUpgradeInclusions, shouldShowLoyaltyPackageUpgradeInclusionsValidityText)
  }, [loyaltyPackageUpgradeInclusions, shouldShowLoyaltyPackageUpgradeInclusionsValidityText])
  const visibleLoyaltyPackageUpgradeBonusInclusions = useMemo(() => {
    return getInclusionsWithoutPackageValidityText(loyaltyPackageUpgradeBonusInclusions, shouldShowLoyaltyPackageUpgradeInclusionsValidityText)
  }, [loyaltyPackageUpgradeBonusInclusions, shouldShowLoyaltyPackageUpgradeInclusionsValidityText])
  const hasLoyaltyRoomUpgradeInclusions = hasPackageInclusionItems(
    loyaltyRoomUpgradeLuxPlusInclusions,
    visibleLoyaltyRoomUpgradeInclusions,
    visibleLoyaltyRoomUpgradeBonusInclusions,
  )
  const hasLoyaltyPackageUpgradeInclusions = hasPackageInclusionItems(
    loyaltyPackageUpgradeLuxPlusInclusions,
    visibleLoyaltyPackageUpgradeInclusions,
    visibleLoyaltyPackageUpgradeBonusInclusions,
  )
  const showAirportTransferOption = typeof airportTransferPriceDifference === 'number' && !!airportTransferInclusion && !!airportTransferBasePackageKey && !!onAirportTransferToggle
  const showBasePackageInclusions = hasInclusions || shouldShowPackageInclusionsValidityText || showAirportTransferOption

  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  // Loyalty upgrades: applying here upgrades every room, which uses one upgrade per room. Only offer
  // it when the member has enough left; they can still upgrade some of the rooms at checkout.
  const isLoyaltyRoomUpgradesAvailable = useUserCanApplyLoyaltyBenefit('hotel_upgrade', rooms.length)

  useLoyaltyOfferPageClearTargetStayUpgrade(packageView, isLoyaltyRoomUpgradesAvailable)

  const activeRoomRate = selectedTargetUpgradePackage?.roomRate ?? pkg.roomRate
  const cancellation = availableForPurchase && activeRoomRate && <FlashCancellationPolicies
    compact={!!selectionPresentation}
    checkInDate={checkIn}
    charterDepartDate={charterBundleRoute ? charterDepartDate : undefined}
    propertyTimezoneOffset={offer.property?.timezoneOffset}
    cancellationPolicy={activeRoomRate.cancellationPolicy}
    numberOfDateChangesAllowed={offer.numberOfDateChangesAllowed}
    showHotelOnly={showHotelOnly || hasAirportTransferPackageInclusions(packageView.package)}
  />

  const quantityLabel = intl.formatMessage({
    id: 'iAkjsUOxc3YG_jjZIjYX',
    defaultMessage: '{roomName}: {packageName}',
    description: 'Accessible label identifying the Flash hotel room and package quantity selector',
  }, { roomName: pkg.roomType.name, packageName: pkg.name })
  const bookNowLabel = intl.formatMessage({
    id: 'mpFHvG15p8mwo5wMs8b2',
    defaultMessage: 'Book now',
    description: 'Call-to-action button text to proceed with booking the current offer',
  })
  const checkAvailabilityLabel = intl.formatMessage({
    id: '_etWrwIdpiw96KvQ_Pzp',
    defaultMessage: 'Check availability',
    description: 'Button text to check availability on the hotel or room booking page',
  })
  const primaryActionLabel = checkIn ? bookNowLabel : checkAvailabilityLabel
  const selectionQuantity = selectionPresentation?.selectionQuantity ?? 1
  const selectPackage = useCallback(() => { if (!quantity) onQuantityChange?.(selectionQuantity) }, [onQuantityChange, quantity, selectionQuantity])
  const isSingleRoomCard = selectionQuantity === 1
  const togglePackage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onQuantityChange?.(event.currentTarget.checked ? selectionQuantity : 0)
  }, [onQuantityChange, selectionQuantity])
  const optionDTitle = <PackageSelectionHeading
    title={pkg.name}
    upgradeApplied={!!selectedTargetUpgradePackage}
    name="flash-package-selection"
    value={pkg.uniqueKey}
    selected={quantity > 0}
    multiple={selectionQuantity > 1}
    disabled={packageLoading || (quantity === 0 && !quantityOptions?.some(option => option.quantity === selectionQuantity))}
    onChange={selectionQuantity === 1 ? selectPackage : togglePackage}
  />

  let optionDInclusions: {
    inclusions: typeof visibleEffectiveInclusions
    bonusInclusions: typeof visibleBonusInclusions
    luxPlusInclusions: typeof luxPlusInclusions
    pkg: App.Package
    showValidity: boolean
  } = { inclusions: visibleEffectiveInclusions, bonusInclusions: visibleBonusInclusions, luxPlusInclusions, pkg, showValidity: shouldShowPackageInclusionsValidityText }
  if (selectedTargetUpgradeType === 'room' && loyaltyRoomUpgradePkg) {
    optionDInclusions = { inclusions: visibleLoyaltyRoomUpgradeInclusions, bonusInclusions: visibleLoyaltyRoomUpgradeBonusInclusions, luxPlusInclusions: loyaltyRoomUpgradeLuxPlusInclusions, pkg: loyaltyRoomUpgradePkg, showValidity: shouldShowLoyaltyRoomUpgradeInclusionsValidityText }
  } else if (selectedTargetUpgradeType === 'package' && loyaltyPackageUpgradePkg) {
    optionDInclusions = { inclusions: visibleLoyaltyPackageUpgradeInclusions, bonusInclusions: visibleLoyaltyPackageUpgradeBonusInclusions, luxPlusInclusions: loyaltyPackageUpgradeLuxPlusInclusions, pkg: loyaltyPackageUpgradePkg, showValidity: shouldShowLoyaltyPackageUpgradeInclusionsValidityText }
  }

  const PackageWrapper = optionD ? React.Fragment : AccommodationSameRoomOptionTileSubgrid

  return (
    <PackageWrapper>
      {suggestedPackageMessage && !isRebrand && !optionD && <SuggestedPackageBannerContainer>
        <MessageBanner kind="promotional" icon={<SolidStarIcon />}>{suggestedPackageMessage}</MessageBanner>
      </SuggestedPackageBannerContainer>}
      {(<>
        {(selectionPresentation) && (<AccommodationPackageSelectionTile
          selected={quantity > 0}
          onSelect={selectPackage}
          selectionDisabled={packageLoading || !quantityOptions?.some(option => option.quantity === selectionQuantity && !option.disabled)}
          data-testid="flash-hotel-package-option"
          data-package-id={pkg.id}
          data-requested-room-count={selectionQuantity}
          data-selected-room-count={quantity}
          ref={impressionRef}

          banner={!!(suggestedPackageMessage || quantityFailure) && <>
            {suggestedPackageMessage && <PackageSelectionRecommendation>{suggestedPackageMessage}</PackageSelectionRecommendation>}
            {quantityFailure && <div role="alert"><MessageBanner kind="critical">{quantityFailure}</MessageBanner></div>}
          </>}
          details={<AccommodationSameRoomOptionTileDetails
            titleContent={optionDTitle}
            upgradeTitleVariant={selectedTargetUpgradeType === 'room' ? 'roomUpgrade' : 'packageUpgrade'}
            selectedLoyaltyTargetUpgrade={selectedLoyaltyTargetUpgrade}
            onViewLoyaltyUpgradedRoomDetails={canViewUpgradedRoomDetails ? onViewUpgradedRoomDetails : undefined}
          >
            {isLoyaltyEnabled && isLoyaltyRoomUpgradesAvailable && <OfferPageLoyaltyStayUpgradeCallOutBanner packageView={packageView} presentation="compact" />}
            {isLoyaltyEnabled && <OfferPageTierProgressionCallOutBanner
              offer={offer}
              packageView={packageView}
              checkIn={checkIn}
              duration={duration}
              hasLoyaltyAccount={hasLoyaltyAccount}
            />}
            {selectedTargetUpgradePackage && upgradedInclusionsHeading}
            {earnings}
            <FlashPackageSelectionInclusions
              offer={offer}
              showAll={showAllInclusions}
              disclosure={inclusionsDisclosure}
              inclusions={optionDInclusions.inclusions}
              bonusInclusions={optionDInclusions.bonusInclusions}
              luxPlusInclusions={optionDInclusions.luxPlusInclusions}
            />
            {optionDInclusions.showValidity && <PackageInclusionsValidityText policyDescription={optionDInclusions.pkg.roomPolicyDescription} selectedOccupancy={selectedOccupancy} />}
            {showAirportTransferOption && <AirportTransferOption
              checked={airportTransferSelected}
              inclusion={airportTransferInclusion}
              packageKey={airportTransferBasePackageKey}
              onChange={onAirportTransferToggle}
              priceDifference={airportTransferPriceDifference}
            />}
            {cancellation}
          </AccommodationSameRoomOptionTileDetails>}
          pricing={<AccommodationSameRoomOptionTilePricing>
            {availableForPurchase && <FlashPackageSelectionPriceDetails packageView={packageView} occupancyOverride={occupancyOverride} onDisplayedPricingChange={reportDisplayedPricing} quoteScope={quoteScope} selectionPricing={selectionPresentation.selectionPricing} />}
          </AccommodationSameRoomOptionTilePricing>}
          actions={<AccommodationSameRoomOptionTileCTA>
            {isSingleRoomCard && <CSSBreakpoint max="tablet">
              <OptionDBooking><OfferBookButton packageView={packageView} size="medium" fit="full-width" disabled={!availableForPurchase || packageLoading || selectionPresentation.purchaseDisabled}>
                {primaryActionLabel}
              </OfferBookButton>{selectionPresentation.showOptions && <FlashPackageSelectionOptions packageView={packageView} presentation="icon" disabled={selectionPresentation.purchaseDisabled} />}
              </OptionDBooking>
            </CSSBreakpoint>}
            {isSingleRoomCard && <CSSBreakpoint min="desktop">
              <FlashPackageRoomQuantity
                value={quantity}
                disabled={packageLoading}
                options={selectionPresentation.quantityOptions}
                quoteKey={quantityQuoteKey}
                label={quantityLabel}
                onChange={selectionPresentation.onQuantityChange}
              />
            </CSSBreakpoint>}
            {!isSingleRoomCard && <FlashPackageRoomQuantity
              value={quantity}
              disabled={packageLoading}
              options={selectionPresentation.quantityOptions}
              quoteKey={quantityQuoteKey}
              label={quantityLabel}
              onChange={selectionPresentation.onQuantityChange}
              initialActionLabel={!roomSelected ? primaryActionLabel : undefined}
              constrainHeading
              headingMin={roomSelected ? undefined : 'tablet'}
            />}
          </AccommodationSameRoomOptionTileCTA>}
        />)}
        {!(selectionPresentation) && (<AccommodationSameRoomOptionTile
          data-testid="flash-hotel-package-option"
          data-package-id={pkg.id}
          ref={impressionRef}
        >
          {focusTargetId && <OfferPageFocusAnchor targetId={focusTargetId} />}
          {isLoyaltyEnabled && (
            <AccommodationSameRoomOptionTileHeaderArea>
              {isLoyaltyRoomUpgradesAvailable && (
                <OfferPageLoyaltyStayUpgradeCallOutBanner packageView={packageView} />
              )}
              <OfferPageTierProgressionCallOutBanner
                offer={offer}
                packageView={packageView}
                checkIn={checkIn}
                duration={duration}
                hasLoyaltyAccount={hasLoyaltyAccount}
              />
            </AccommodationSameRoomOptionTileHeaderArea>
          )}
          <AccommodationSameRoomOptionTileDetails
            packageNumber={packagePosition}
            optionName={pkg.name}
            promotionLabel={suggestedPackagePromotionLabel}
            upgradeTitleVariant={selectedTargetUpgradeType === 'room' ? 'roomUpgrade' : 'packageUpgrade'}
            selectedLoyaltyTargetUpgrade={selectedTargetUpgradePackage ? { name: appliedUpgradeDisplayName } : undefined}
            onViewLoyaltyUpgradedRoomDetails={canViewUpgradedRoomDetails ? onViewUpgradedRoomDetails : undefined}
          >
            {cancellation}
            {showBasePackageInclusions && (
              <InclusionsContainer>
                <BasePackageInclusions className={clsx({ 'show': !selectedTargetUpgradePackage })}>
                  {hasInclusions && (
                    <FlashHotelInclusionsToggle
                      packagePosition={packagePosition}
                      open={inclusionsOpen}
                      onToggle={onInclusionsToggle}
                    >
                      <FlashHotelRateInclusionsContent
                        offer={offer}
                        inclusions={visibleEffectiveInclusions}
                        bonusInclusions={visibleBonusInclusions}
                        luxPlusInclusions={luxPlusInclusions}
                        rooms={rooms}
                        showReturnFlightsInclusion={showReturnFlightsInclusion}
                      />
                    </FlashHotelInclusionsToggle>
                  )}
                  {shouldShowPackageInclusionsValidityText && (
                    <PackageInclusionsValidityText policyDescription={pkg.roomPolicyDescription} selectedOccupancy={selectedOccupancy} />
                  )}
                  {showAirportTransferOption && (
                    <AirportTransferOption
                      checked={airportTransferSelected}
                      inclusion={airportTransferInclusion}
                      packageKey={airportTransferBasePackageKey}
                      onChange={onAirportTransferToggle}
                      priceDifference={airportTransferPriceDifference}
                    />
                  )}
                </BasePackageInclusions>
                {loyaltyRoomUpgradePkg && (
                  <TargetUpgradePackageInclusions className={clsx({ 'show': selectedTargetUpgradeType === 'room' })}>
                    {hasLoyaltyRoomUpgradeInclusions && upgradedInclusionsHeading}
                    {hasLoyaltyRoomUpgradeInclusions && (
                      <FlashHotelInclusionsToggle
                        packagePosition={packagePosition}
                        open={inclusionsOpen}
                        onToggle={onInclusionsToggle}
                      >
                        <FlashHotelRateInclusionsContent
                          offer={offer}
                          inclusions={visibleLoyaltyRoomUpgradeInclusions}
                          bonusInclusions={visibleLoyaltyRoomUpgradeBonusInclusions}
                          luxPlusInclusions={loyaltyRoomUpgradeLuxPlusInclusions}
                          rooms={rooms}
                          showReturnFlightsInclusion={showReturnFlightsInclusion}
                        />
                      </FlashHotelInclusionsToggle>
                    )}
                    {shouldShowLoyaltyRoomUpgradeInclusionsValidityText && (
                      <PackageInclusionsValidityText policyDescription={loyaltyRoomUpgradePkg.roomPolicyDescription} selectedOccupancy={selectedOccupancy} />
                    )}
                  </TargetUpgradePackageInclusions>
                )}
                {loyaltyPackageUpgradePkg && (
                  <TargetUpgradePackageInclusions className={clsx({ 'show': selectedTargetUpgradeType === 'package' })}>
                    {hasLoyaltyPackageUpgradeInclusions && upgradedInclusionsHeading}
                    {hasLoyaltyPackageUpgradeInclusions && (
                      <FlashHotelInclusionsToggle
                        packagePosition={packagePosition}
                        open={inclusionsOpen}
                        onToggle={onInclusionsToggle}
                      >
                        <FlashHotelRateInclusionsContent
                          offer={offer}
                          inclusions={visibleLoyaltyPackageUpgradeInclusions}
                          bonusInclusions={visibleLoyaltyPackageUpgradeBonusInclusions}
                          luxPlusInclusions={loyaltyPackageUpgradeLuxPlusInclusions}
                          rooms={rooms}
                          showReturnFlightsInclusion={showReturnFlightsInclusion}
                        />
                      </FlashHotelInclusionsToggle>
                    )}
                    {shouldShowLoyaltyPackageUpgradeInclusionsValidityText && (
                      <PackageInclusionsValidityText policyDescription={loyaltyPackageUpgradePkg.roomPolicyDescription} selectedOccupancy={selectedOccupancy} />
                    )}
                  </TargetUpgradePackageInclusions>
                )}
              </InclusionsContainer>
            )}
          </AccommodationSameRoomOptionTileDetails>

          <AccommodationSameRoomOptionTilePricing>
            {!children && availableForPurchase && <FlashHotelOptionPriceDetails
              packageView={packageView}
              roomsBeforeSurcharge={roomsBeforeSurcharge}
              onDisplayedPricingChange={reportDisplayedPricing}
            />}
          </AccommodationSameRoomOptionTilePricing>

          <AccommodationSameRoomOptionTileCTA>
            {children}
            {!children && availableForPurchase && (
              <Suspense fallback={null}>
                <FlashHotelOptionCTAs packageView={packageView} />
              </Suspense>
            )}
          </AccommodationSameRoomOptionTileCTA>
        </AccommodationSameRoomOptionTile>)}
      </>)}
    </PackageWrapper>
  )
}

type MappedProps = Pick<Props,
  | 'showHotelOnly'
  | 'offer'
  | 'packageOptions'
  | 'checkIn'
  | 'duration'
  | 'rooms'
  | 'fareClass'
  | 'charterBundleRoute'
  | 'flightOrigin'
  | 'availableForPurchase'
  | 'packageLoading'
  | 'flights'
  | 'isInclusionsValidityEnabled'
>

export default withOfferPageAndReduxState((state, appState, ownProps: Props): MappedProps => ({
  showHotelOnly: getShouldQualifyOfferHotelCancellation(state),
  offer: state.offer as App.HotelOffer,
  packageOptions: getDurationPackageOptions(state, appState),
  checkIn: state.checkInDate,
  duration: state.duration,
  rooms: state.rooms,
  fareClass: toBaseFareClass(state.fareClass),
  charterBundleRoute: getEffectiveCharterBundleRoute(state),
  flightOrigin: state.airport,
  availableForPurchase: isAvailableForPurchase(state, appState),
  packageLoading: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey],
  flights: state.flights,
  isInclusionsValidityEnabled: !!appState.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.croInclusionValidity],
}), FlashHotelRateOption)
